import type { Moment } from 'moment-timezone';
import moment from 'moment-timezone';

import { DateRangeValueType } from '@core/date-range-types';
import { DataInterval, ReportDateFilter } from '@core/reports-types';
import { findEndDateOfPredefinedRange, findStartDateOfPredefinedRange } from '@core/reports-util';
import { resolveWeekType } from '@core/time-legacy';

const formatWeeklyLabels = (
  date: Moment,
  weekEnd: Moment,
  weekEndClamped: Moment,
): { primary: string; secondary: string } => {
  const primary = date.isSame(weekEndClamped, 'day')
    ? date.format('Do MMM')
    : date.month() !== weekEndClamped.month()
      ? `${date.format('Do MMM')} - ${weekEndClamped.format('Do MMM')}`
      : `${date.format('Do')} - ${weekEndClamped.format('Do MMM')}`;

  const secondary =
    date.year() !== weekEndClamped.year()
      ? `${date.format('YYYY')} - ${weekEndClamped.format('YYYY')}`
      : date.format('YYYY');

  return {
    primary,
    secondary,
  };
};

export const formatDateWithInterval = (
  value: number | string,
  dateFilter: ReportDateFilter,
  interval: DataInterval = DataInterval.Daily,
): { primary: string; secondary: string } => {
  const rangeStartDate = findStartDateOfPredefinedRange(dateFilter);
  const rangeEndDate = findEndDateOfPredefinedRange(dateFilter);

  const exactDate = moment(value);
  const date = rangeStartDate && exactDate.isBefore(rangeStartDate) ? rangeStartDate : exactDate;

  switch (interval) {
    case DataInterval.Quarterly: {
      const quarterEnd = date.clone().endOf('Q');
      const quarterEndClamped =
        rangeEndDate && quarterEnd.isAfter(rangeEndDate) ? rangeEndDate : quarterEnd;

      const primary =
        date.isSame(quarterEndClamped, 'day') || date.isSameOrAfter(rangeEndDate, 'day')
          ? date.format('Do MMM')
          : `${date.format('Do MMM')} - ${quarterEndClamped.format('Do MMM')}`;

      return {
        primary,
        secondary: `Q${date.format('Q')} ${date.format('YYYY')}`,
      };
    }

    case DataInterval.Monthly: {
      const monthEnd = date.clone().endOf('month');
      const monthEndClamped =
        rangeEndDate && monthEnd.isAfter(rangeEndDate) ? rangeEndDate : monthEnd;

      const month = date.clone().format('MMM');

      const primary =
        date.isSame(monthEndClamped, 'day') || date.isSameOrAfter(rangeEndDate, 'day')
          ? `${date.format('Do MMM')}`
          : `${date.format('Do')} - ${monthEndClamped.format('Do')} ${month}`;

      return {
        primary,
        secondary: date.format('YYYY'),
      };
    }

    case DataInterval.Weekly: {
      const weekEnd = date.clone().endOf(resolveWeekType());
      const weekEndClamped = rangeEndDate
        ? weekEnd.isAfter(rangeEndDate)
          ? rangeEndDate
          : weekEnd
        : weekEnd;

      return formatWeeklyLabels(date, weekEnd, weekEndClamped);
    }

    default:
    case DataInterval.Daily:
      return {
        primary: date.format('Do MMM (ddd)'),
        secondary: date.format('YYYY'),
      };
  }
};

export const formatSingleTickAxisLabel =
  (interval: DataInterval) =>
  (value: number | string): string => {
    const { primary, secondary } = formatDateWithInterval(
      value,
      {
        dateRange: DateRangeValueType.AllTime,
        dateStart: null,
        dateEnd: null,
      },
      interval,
    );
    return `${primary}\n${secondary}`;
  };
